import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PromotionState } from './types'
import { PromotionService } from '../services/PromotionService'
import { PromotionDealerType } from '../../../types/types'

const initialState: PromotionState = {
  // state
  isPromotionActive: true,
  promotionDealerType: 'all',
  excludeGoTripPage: false,
  promotionInsuranceIds: [],
  promotionDealerIds: [],
  isGetAllPromotionsLoading: false,
  isGetAllInsurancePlansLoading: false,
  isExportPromotionsLoading: false,
  isExportPromoCodesLoading: false,
  isCreatePromotionLoading: false,
  isUpdatePromotionLoading: false,
  isGetPromotionDetailLoading: false,
  isGetDiscountDetailLoading: false,
  isGetAllPromotionInsurancePlansLoading: false,
  isGetAllPromotionCodesLoading: false,
  isUpdatePromotionStatusLoading: false,
  isGetPromotionDealerDetailLoading: false,
  // response
  fetchedAllInsurancePlans: []
}

const slice = createSlice({
  name: 'promotion',
  initialState,
  reducers: {
    togglePromotionActive: (state) => {
      state.isPromotionActive = !state.isPromotionActive
    },
    setPromotionInsuranceIds: (state, action: PayloadAction<string[]>) => {
      const allIds = action.payload

      state.promotionInsuranceIds = []

      state.promotionInsuranceIds.push(...allIds)
    },
    removeInsuranceId: (state, action: PayloadAction<string>) => {
      const itemId = action.payload

      const index = state.promotionInsuranceIds.indexOf(itemId)

      if (index !== -1) {
        state.promotionInsuranceIds.splice(index, 1)
      }
    },
    setPromotionDealerIds: (state, action: PayloadAction<string[]>) => {
      state.promotionDealerIds = action.payload
    },
    setPromotionDealerType: (state, action: PayloadAction<PromotionDealerType>) => {
      state.promotionDealerType = action.payload
    },
    toggleExcludeGoTripPage: (state) => {
      state.excludeGoTripPage = !state.excludeGoTripPage
    },
    resetPromotionState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(PromotionService.getAllPromotionsAsyncThunk.pending, (state) => {
        state.isGetAllPromotionsLoading = true
      })
      .addCase(PromotionService.getAllPromotionsAsyncThunk.rejected, (state) => {
        state.isGetAllPromotionsLoading = false
      })
      .addCase(PromotionService.getAllPromotionsAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllPromotionsLoading = false
        state.fetchedAllPromotions = action.payload
      })
      .addCase(PromotionService.getAllInsurancePlansAsyncThunk.pending, (state) => {
        state.isGetAllInsurancePlansLoading = true
      })
      .addCase(PromotionService.getAllInsurancePlansAsyncThunk.rejected, (state) => {
        state.isGetAllInsurancePlansLoading = false
      })
      .addCase(PromotionService.getAllInsurancePlansAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllInsurancePlansLoading = false
        state.fetchedAllInsurancePlans = action.payload
      })
      .addCase(PromotionService.exportPromotionsAsyncThunk.pending, (state) => {
        state.isExportPromotionsLoading = true
      })
      .addCase(PromotionService.exportPromotionsAsyncThunk.rejected, (state) => {
        state.isExportPromotionsLoading = false
      })
      .addCase(PromotionService.exportPromotionsAsyncThunk.fulfilled, (state) => {
        state.isExportPromotionsLoading = false
      })
      .addCase(PromotionService.exportPromoCodesAsyncThunk.pending, (state) => {
        state.isExportPromoCodesLoading = true
      })
      .addCase(PromotionService.exportPromoCodesAsyncThunk.rejected, (state) => {
        state.isExportPromoCodesLoading = false
      })
      .addCase(PromotionService.exportPromoCodesAsyncThunk.fulfilled, (state) => {
        state.isExportPromoCodesLoading = false
      })
      .addCase(PromotionService.createPromotionAsyncThunk.pending, (state) => {
        state.isCreatePromotionLoading = true
      })
      .addCase(PromotionService.createPromotionAsyncThunk.rejected, (state) => {
        state.isCreatePromotionLoading = false
      })
      .addCase(PromotionService.createPromotionAsyncThunk.fulfilled, (state) => {
        state.isCreatePromotionLoading = false
      })
      .addCase(PromotionService.updatePromotionAsyncThunk.pending, (state) => {
        state.isUpdatePromotionLoading = true
      })
      .addCase(PromotionService.updatePromotionAsyncThunk.rejected, (state) => {
        state.isUpdatePromotionLoading = false
      })
      .addCase(PromotionService.updatePromotionAsyncThunk.fulfilled, (state) => {
        state.isUpdatePromotionLoading = false
      })
      .addCase(PromotionService.getPromotionDetailAsyncThunk.pending, (state) => {
        state.isGetPromotionDetailLoading = true
      })
      .addCase(PromotionService.getPromotionDetailAsyncThunk.rejected, (state) => {
        state.isGetPromotionDetailLoading = false
      })
      .addCase(PromotionService.getPromotionDetailAsyncThunk.fulfilled, (state, action) => {
        state.isGetPromotionDetailLoading = false
        state.fetchedPromotionDetail = action.payload
        state.isPromotionActive = action.payload.isActive
        state.promotionTimestamp = action.payload
      })
      .addCase(PromotionService.getDiscountDetailAsyncThunk.pending, (state) => {
        state.isGetDiscountDetailLoading = true
      })
      .addCase(PromotionService.getDiscountDetailAsyncThunk.rejected, (state) => {
        state.isGetDiscountDetailLoading = false
      })
      .addCase(PromotionService.getDiscountDetailAsyncThunk.fulfilled, (state, action) => {
        state.isGetDiscountDetailLoading = false
        state.fetchedDiscountDetail = action.payload
        state.promotionTimestamp = action.payload
      })
      .addCase(PromotionService.getAllPromotionInsurancePlansAsyncThunk.pending, (state) => {
        state.isGetAllPromotionInsurancePlansLoading = true
      })
      .addCase(PromotionService.getAllPromotionInsurancePlansAsyncThunk.rejected, (state) => {
        state.isGetAllPromotionInsurancePlansLoading = false
      })
      .addCase(PromotionService.getAllPromotionInsurancePlansAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllPromotionInsurancePlansLoading = false
        state.fetchedAllPromotionInsurancePlans = action.payload
        state.promotionInsuranceIds = action.payload.insurancePlans
        state.promotionTimestamp = action.payload
      })
      .addCase(PromotionService.getAllPromotionCodesAsyncThunk.pending, (state) => {
        state.isGetAllPromotionCodesLoading = true
      })
      .addCase(PromotionService.getAllPromotionCodesAsyncThunk.rejected, (state) => {
        state.isGetAllPromotionCodesLoading = false
      })
      .addCase(PromotionService.getAllPromotionCodesAsyncThunk.fulfilled, (state, action) => {
        state.isGetAllPromotionCodesLoading = false
        state.fetchedAllPromotionCodes = action.payload
        state.promotionTimestamp = action.payload
      })
      .addCase(PromotionService.updatePromotionStatusAsyncThunk.pending, (state) => {
        state.isUpdatePromotionStatusLoading = true
      })
      .addCase(PromotionService.updatePromotionStatusAsyncThunk.rejected, (state) => {
        state.isUpdatePromotionStatusLoading = false
      })
      .addCase(PromotionService.updatePromotionStatusAsyncThunk.fulfilled, (state) => {
        state.isUpdatePromotionStatusLoading = false
      })
      .addCase(PromotionService.getPromotionDealerDetailAsyncThunk.pending, (state) => {
        state.isGetPromotionDealerDetailLoading = true
      })
      .addCase(PromotionService.getPromotionDealerDetailAsyncThunk.rejected, (state) => {
        state.isGetPromotionDealerDetailLoading = false
      })
      .addCase(PromotionService.getPromotionDealerDetailAsyncThunk.fulfilled, (state, action) => {
        state.isGetPromotionDealerDetailLoading = false
        state.fetchedPromotionDealerIds = action.payload
        state.promotionDealerIds = action.payload.dealerIdList
        state.excludeGoTripPage = action.payload.excludeGoTripPage
        state.promotionDealerType = action.payload.dealerIdList.length > 0 ? 'some' : 'all'
      })
  }
})

export const {
  resetPromotionState,
  setPromotionInsuranceIds,
  togglePromotionActive,
  removeInsuranceId,
  setPromotionDealerIds,
  setPromotionDealerType,
  toggleExcludeGoTripPage
} = slice.actions
export default slice.reducer
